import React, { useState } from 'react';
import { Modal, Upload, Button, message, Input, Tabs, Image, Row, Col, Card } from 'antd';
import { UploadOutlined, LinkOutlined, InboxOutlined } from '@ant-design/icons';
import apiClient from '../services/api';

const { Dragger } = Upload;
const { TabPane } = Tabs;

const ImageUploadModal = ({ visible, onCancel, onInsert }) => {
  const [activeTab, setActiveTab] = useState('upload');
  const [imageUrl, setImageUrl] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  // Xử lý upload file
  const handleUpload = async (file) => {
    setLoading(true);
    
  const formData = new FormData();
  formData.append('image', file);
    
    try {
      //console.log('🔄 Uploading file:', file.name);

      // Use axios instance so baseURL and auth headers are applied
      const response = await apiClient.post('/api/uploads/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const result = response.data;
      //console.log('✅ Upload result:', result);

      let uploadedUrl = result?.data?.url;

      // If server returned http but site is https, normalize to https to avoid mixed content
      if (uploadedUrl && uploadedUrl.startsWith('http://') && window.location.protocol === 'https:') {
        uploadedUrl = uploadedUrl.replace(/^http:\/\//i, 'https://');
      }

      if (uploadedUrl) {
        setUploadedFiles(prev => [...prev, {
          uid: Date.now(),
          name: file.name,
          url: uploadedUrl,
          status: 'done'
        }]);

        message.success(`Upload ${file.name} thành công!`);
        return uploadedUrl;
      }
      throw new Error('Không nhận được URL từ server');
    } catch (error) {
      //console.error('❌ Upload error:', error);
      message.error(`Upload ${file.name} thất bại: ${error.message}`);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Upload properties
  const uploadProps = {
    name: 'image',
    multiple: true,
    accept: 'image/*',
    customRequest: ({ file, onSuccess, onError }) => {
      handleUpload(file)
        .then(url => {
          if (url) {
            onSuccess();
          } else {
            onError();
          }
        })
        .catch(onError);
    },
    onChange(info) {
      const { status } = info.file;
      if (status === 'done') {
        message.success(`${info.file.name} upload thành công.`);
      } else if (status === 'error') {
        message.error(`${info.file.name} upload thất bại.`);
      }
    },
  };

  // Chèn hình ảnh từ URL
  const insertImageUrl = () => {
    if (imageUrl.trim()) {
      onInsert(imageUrl.trim());
      setImageUrl('');
      onCancel();
    } else {
      message.warning('Vui lòng nhập URL hình ảnh!');
    }
  };

  // Chèn hình ảnh đã upload
  const insertUploadedImage = (url) => {
    onInsert(url);
    onCancel();
  };

  return (
    <Modal
      title="Chèn hình ảnh"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab={<span><UploadOutlined />Upload từ máy tính</span>} key="upload">
          <div style={{ padding: '16px 0' }}>
            <Dragger {...uploadProps} style={{ marginBottom: 16 }}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click hoặc kéo thả file vào khu vực này để upload</p>
              <p className="ant-upload-hint">
                Hỗ trợ upload một hoặc nhiều file. Chỉ chấp nhận file hình ảnh (JPG, PNG, GIF, WebP).
              </p>
            </Dragger>
            
            {/* Hiển thị hình ảnh đã upload */}
            {uploadedFiles.length > 0 && (
              <div>
                <h4>Hình ảnh đã upload:</h4>
                <Row gutter={[16, 16]}>
                  {uploadedFiles.map((file) => (
                    <Col span={6} key={file.uid}>
                      <Card
                        hoverable
                        cover={
                          <Image
                            src={file.url}
                            alt={file.name}
                            style={{ height: 120, objectFit: 'cover' }}
                          />
                        }
                        actions={[
                          <Button 
                            type="primary" 
                            size="small"
                            onClick={() => insertUploadedImage(file.url)}
                          >
                            Chèn vào bài
                          </Button>
                        ]}
                      >
                        <Card.Meta 
                          title={file.name.length > 15 ? `${file.name.substring(0, 15)}...` : file.name}
                          description="Click để chèn"
                        />
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
            )}
          </div>
        </TabPane>
        
        <TabPane tab={<span><LinkOutlined />Từ URL</span>} key="url">
          <div style={{ padding: '16px 0' }}>
            <Input
              placeholder="https://example.com/image.jpg"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              style={{ marginBottom: 16 }}
              onPressEnter={insertImageUrl}
            />
            
            {imageUrl && (
              <div style={{ marginBottom: 16 }}>
                <h4>Xem trước:</h4>
                <Image
                  src={imageUrl}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: 300 }}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RUG8A+EQEsRIiREREiKQEQIQoQoUkRIiihSBEKKKFFEiCJFhEiKIkUgRCBCEQoRgRAKIUQgRBEK..."
                />
              </div>
            )}
            
            <Button type="primary" onClick={insertImageUrl} block>
              Chèn hình ảnh từ URL
            </Button>
          </div>
        </TabPane>
      </Tabs>
    </Modal>
  );
};

export default ImageUploadModal;
